"use client"

import { useMemo, type ReactNode } from "react"
import { NextIntlClientProvider, useLocale, useMessages } from "next-intl"
import type { Messages } from "./pick-messages"

/**
 * Adds `messages` to the ones inherited from the provider above.
 *
 * A nested `NextIntlClientProvider` does not do that by itself: a `messages`
 * prop REPLACES the inherited catalog (use-intl `IntlProvider`), so a page that
 * rendered one directly would have to ship the layout's namespaces a second
 * time. This reads the parent's messages on the client and merges per
 * namespace, the passed keys winning, so a page ships only what the layout
 * does not already carry. Locale, time zone and formats stay inherited.
 */
export function MergeMessagesProvider({
  messages,
  children,
}: {
  messages: Messages
  children: ReactNode
}) {
  const locale = useLocale()
  const inherited = useMessages() as Messages
  const merged = useMemo(
    () => ({
      ...inherited,
      ...Object.fromEntries(
        Object.keys(messages).map((namespace) => [
          namespace,
          { ...inherited[namespace], ...messages[namespace] },
        ])
      ),
    }),
    [inherited, messages]
  )
  return (
    <NextIntlClientProvider locale={locale} messages={merged}>
      {children}
    </NextIntlClientProvider>
  )
}
